import React, { useState } from 'react';
import classNames from 'classnames';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';
import {
  extractDigits,
  NeuroFinancialAssistanceV2Dto,
  neuroFinancialAssistanceV2Fields,
} from '@tempus/patient-forms-service-shared';
import { PortalViewsEnum } from '@tempus/portal-service-shared';
import { NavigationFormProps } from '../../../custom-to-do-list/components/NavigatorChecklist';
import Form from '../../shared/Form';
import Logo from '../../shared/Logo';
import { deleteUnmetConditionalFields, deleteFalsyStringFields, combineFormConfigs } from '../../shared/utils';
import CheckYourEmailAndPhone from '../../shared/CheckYourEmailAndPhone';
import NeuroStandaloneFormUnknownError from '../../shared/psychiatry/NeuroStandaloneFormUnknownError';
import PatientStandaloneFormAuthenticationManager, {
  PatientStandaloneFormAuthenticationValues,
} from '../../shared/PatientStandaloneFormAuthenticationFlow';
import standaloneStyles from '../../shared/standalone-styles.module.css';
import { PatientFormsTitleInjection } from '../../../custom-to-do-list/components/PatientFormsTitleInjection';
import fetchHelper from '../../../../utils';
import config from '../../../../../config';
import { CachedFile } from '../../../../portal/components/UploadZone';
import { buildBase64DataUrl } from '../../../../portal/utils/getBase64File';
import NeuroFinancialAssistanceFormV2, {
  neuroFinancialAssistancePatientInfoFields,
  neuroFinancialAssistanceConsentFields,
  neuroFinancialAssistanceSignatureFields,
} from './NeuroFinancialAssistanceFormV2';
import { AuthenticationType } from '../../../../api/AuthenticationType';
import { FinancialAssistanceStatuses } from 'requisition-service-shared';
import Loader from '@tcl-boron-prefabs/loader';
import logger from '../../../../../utils/logger';
import {
  NeuroFinancialAssistanceSubmissionError,
  NeuroFinancialAssistanceSubmitted,
  NeuroFinancialAssistanceAlreadySubmitted,
} from '../components';

export const NEURO_FINANCIAL_ASSISTANCE_V2_KEY = 'NEUROLOGY_FINANCIAL_ASSISTANCE_V2';
export const NEURO_FINANCIAL_ASSISTANCE_V2_TITLE = 'Neurology Financial Assistance';

type NeuroFinancialAssistanceStartFormResponseV2 = {
  faStatus: FinancialAssistanceStatuses | null;
};

type NeuroFinancialAssistanceSubmitFormResponseV2 = {
  faStatus: FinancialAssistanceStatuses | null;
};

type NeuroFinancialAssistanceStartFormPropsV2 = PatientStandaloneFormAuthenticationValues &
  NeuroFinancialAssistanceStartFormResponseV2;

const NeuroFinancialAssistanceWrapperV2: React.FunctionComponent<NavigationFormProps> = (props) => {
  const [showSuccessAlert, setShowAlert] = useState<boolean>(false);
  const [startFormProps, setStartFormProps] = useState<NeuroFinancialAssistanceStartFormPropsV2 | null>(null);
  const [cachedFile, setCachedFile] = useState<CachedFile | undefined>();
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isGenericError, setIsGenericError] = useState<boolean>(false);
  const [isSubmissionError, setIsSubmissionError] = useState<boolean>(false);
  const [isLinkSent, setIsLinkSent] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [faStatus, setFaStatus] = useState<FinancialAssistanceStatuses | undefined>();

  const onSuccessfulAuthentication = async (patientLookupValues: PatientStandaloneFormAuthenticationValues) => {
    setIsLinkSent(false);
    setIsGenericError(false);
    setIsSubmissionError(false);
    const {
      patientId,
      patientFirstName,
      patientLastName,
      patientDateOfBirth,
      patientEmail,
      patientPhoneNumber,
    } = patientLookupValues;

    try {
      setIsLoading(true);
      const data = await fetchHelper<NeuroFinancialAssistanceStartFormResponseV2>(`/form/standalone/start`, {
        baseUrl: config.patientWebServiceBaseUrl,
        method: 'POST',
        headerOverrides: {
          'x-selected-portal-view': PortalViewsEnum.PSYCHIATRY,
        },
        body: {
          formType: NEURO_FINANCIAL_ASSISTANCE_V2_KEY,
          patientId,
          patientFirstName,
          patientLastName,
          patientDateOfBirth,
          patientEmail,
          patientPhoneNumber: extractDigits(patientPhoneNumber),
        },
        authenticationType: AuthenticationType.MANUAL,
        authToken: patientLookupValues.identityVerificationToken,
      });
      setStartFormProps({
        ...patientLookupValues,
        ...data,
      });
    } catch (error) {
      setIsGenericError(true);
      setStartFormProps(null);
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (values: any) => {
    if (!startFormProps) {
      throw new Error('Patient lookup response is required for submission');
    }

    const formConfig = combineFormConfigs(
      [
        ...neuroFinancialAssistancePatientInfoFields,
        ...neuroFinancialAssistanceConsentFields,
        ...neuroFinancialAssistanceSignatureFields,
      ],
      neuroFinancialAssistanceV2Fields,
    );
    deleteUnmetConditionalFields(values, formConfig);
    deleteFalsyStringFields(values);

    const dto: NeuroFinancialAssistanceV2Dto = {
      ...values,
      extenuatingCircumstances: values.extenuatingCircumstances || [],
      patientFirstName: startFormProps.patientFirstName,
      patientLastName: startFormProps.patientLastName,
      patientDateOfBirth: startFormProps.patientDateOfBirth,
      patientEmail: startFormProps.patientEmail,
      patientPhoneNumber: extractDigits(startFormProps.patientPhoneNumber),
      acceptableProofOfHouseholdIncome: buildBase64DataUrl(cachedFile?.type ?? '', cachedFile?.content ?? ''),
    };

    try {
      const response = await fetchHelper<NeuroFinancialAssistanceSubmitFormResponseV2>(`/form/standalone/submit`, {
        method: 'POST',
        baseUrl: config.patientWebServiceBaseUrl,
        headerOverrides: {
          'x-selected-portal-view': PortalViewsEnum.PSYCHIATRY,
        },
        body: {
          formType: NEURO_FINANCIAL_ASSISTANCE_V2_KEY,
          patientId: startFormProps.patientId,
          data: dto,
        },
        authenticationType: AuthenticationType.MANUAL,
        authToken: startFormProps?.identityVerificationToken,
      });

      setFaStatus(response?.faStatus ?? undefined);
      setIsSubmitted(true);
    } catch (e: any) {
      logger.error(e, 'Error submitting Neuro Financial Assistance V2 form');
      setIsSubmissionError(true);
    }
  };

  const showFormState = startFormProps && !startFormProps.faStatus;
  const isAlreadySubmitted = startFormProps && startFormProps.faStatus;

  const clearForm = () => {
    setStartFormProps(null);
    setIsLinkSent(false);
    setIsGenericError(false);
    setCachedFile(undefined);
  };

  return (
    <PatientFormsTitleInjection>
      <div>
        <div className={classNames(typography.body, standaloneStyles.screen)}>
          <div className={standaloneStyles.header}>
            <div className={standaloneStyles.headerLogo}>
              <Logo />
            </div>
          </div>
          <div className={standaloneStyles.content}>
            <div className={standaloneStyles.titleContainer}>
              <p className={standaloneStyles.title}>{NEURO_FINANCIAL_ASSISTANCE_V2_TITLE}</p>
              {startFormProps?.referenceId && (
                <p className={standaloneStyles.subtitle}>
                  Form Reference ID: <strong>{startFormProps.referenceId}</strong>
                </p>
              )}
            </div>
            {isLinkSent && <CheckYourEmailAndPhone variant="fa" />}
            <PatientStandaloneFormAuthenticationManager
              onAuthenticationSuccess={onSuccessfulAuthentication}
              onAuthenticationError={() => {
                clearForm();
                setIsGenericError(true);
              }}
              onClear={clearForm}
              onSendTokenizedLinkDispatched={() => {
                setIsLinkSent(true);
                setIsGenericError(false);
                setIsSubmissionError(false);
              }}
              onSendTokenizedLinkError={() => {
                clearForm();
                setIsLinkSent(false);
                setIsGenericError(true);
                setIsSubmissionError(false);
              }}
              formType={NEURO_FINANCIAL_ASSISTANCE_V2_KEY}
            />
            {isGenericError ? (
              <NeuroStandaloneFormUnknownError referenceId={startFormProps?.referenceId} />
            ) : isSubmissionError ? (
              <NeuroFinancialAssistanceSubmissionError
                onRetry={() => {
                  setIsSubmissionError(false);
                  setIsSubmitted(false);
                  setCachedFile(undefined);
                }}
                referenceId={startFormProps?.referenceId}
              />
            ) : isSubmitted ? (
              <NeuroFinancialAssistanceSubmitted approvalStatus={faStatus} referenceId={startFormProps?.referenceId} />
            ) : isAlreadySubmitted ? (
              <NeuroFinancialAssistanceAlreadySubmitted referenceId={startFormProps?.referenceId} />
            ) : isLoading ? (
              <div className={standaloneStyles.loaderContainer}>
                <Loader />
              </div>
            ) : showFormState ? (
              <Form onSubmit={onSubmit}>
                <NeuroFinancialAssistanceFormV2
                  showAlert={showSuccessAlert}
                  setShowAlert={setShowAlert}
                  cachedFile={cachedFile}
                  setCachedFile={setCachedFile}
                  {...props}
                />
              </Form>
            ) : null}
          </div>
        </div>
      </div>
    </PatientFormsTitleInjection>
  );
};

export default NeuroFinancialAssistanceWrapperV2;
